import { useEffect, useState } from 'react';
import { fetchOverviewSiteConditionPosture } from '../domain/continuousSafety/overviewSiteConditionSummary';
import type { OverTimeWindowId } from '../domain/sellableSiteExperience/overTimePresenter';
import { QUALIFYING_ENTRY_POLL_MS } from '../domain/showcase/qualifyingEntryActivity';
import {
  assembleShowcaseConditionBundle,
  selectedShowcaseCondition,
  type ShowcaseConditionBundle,
  type ShowcaseConditionPresentation,
} from '../domain/showcase/showcaseConditionData';
import { showcaseCustomerTimeZone } from '../domain/showcase/showcaseCustomerParity';
import type { ShowcaseRoomBCondition, ShowcaseSource } from '../domain/showcase/showcasePresentation';

export type ShowcaseConditionData = {
  status: 'idle' | 'loading' | 'ready' | 'unavailable';
  bundle: ShowcaseConditionBundle | null;
  selected: ShowcaseConditionPresentation | null;
  selectedWindowId: OverTimeWindowId;
  setSelectedWindowId: (windowId: OverTimeWindowId) => void;
};

const DEFAULT_WINDOW: OverTimeWindowId = 'rolling_24h';

const lastGoodBundleBySite = new Map<string, ShowcaseConditionBundle>();

/** A completed count string, including "0 occurrences", is a usable measurement. */
function presentationHasCount(condition: ShowcaseConditionPresentation | null | undefined): boolean {
  return Boolean(String(condition?.lastCompletedValue || '').trim());
}

function bundleHasCompletedCount(bundle: ShowcaseConditionBundle | null | undefined): boolean {
  return Boolean(
    bundle
    && (
      presentationHasCount(bundle.boothRae)
      || presentationHasCount(bundle.roomBRae)
      || presentationHasCount(bundle.roomBProximity)
    ),
  );
}

export function resetShowcaseConditionDataCache() {
  lastGoodBundleBySite.clear();
}

export function useShowcaseConditionData(args: {
  active: boolean;
  siteId?: string | null;
  source: ShowcaseSource;
  roomBCondition: ShowcaseRoomBCondition;
  boothCameraId?: string | null;
  roomBCameraId?: string | null;
  roomBRaeConditionId?: string | null;
  roomBProximityConditionId?: string | null;
  timeZone?: string | null;
}): ShowcaseConditionData {
  const [status, setStatus] = useState<ShowcaseConditionData['status']>('idle');
  const [bundle, setBundle] = useState<ShowcaseConditionBundle | null>(null);
  const [selectedWindowId, setSelectedWindowId] = useState<OverTimeWindowId>(DEFAULT_WINDOW);
  const siteId = String(args.siteId || '').trim();

  useEffect(() => {
    if (!args.active || !siteId) {
      setStatus('idle');
      setBundle(null);
      return undefined;
    }

    const cached = lastGoodBundleBySite.get(siteId) || null;
    if (cached) {
      setBundle((current) => current || cached);
      setStatus('ready');
    }

    let cancelled = false;
    let timer: number | undefined;
    setStatus((current) => (current === 'ready' || cached ? 'ready' : 'loading'));

    const retainLastGood = () => {
      setStatus((current) => (current === 'ready' || lastGoodBundleBySite.has(siteId) ? 'ready' : 'unavailable'));
      setBundle((current) => current || lastGoodBundleBySite.get(siteId) || null);
    };

    const load = () => {
      void fetchOverviewSiteConditionPosture(siteId, {
        selectedOverTimeWindowId: selectedWindowId,
      }).then((result) => {
        if (cancelled) return;
        if (result.status !== 'success') {
          retainLastGood();
          return;
        }
        const next = assembleShowcaseConditionBundle({
          summary: {
            ...result.data,
            siteTimezone: showcaseCustomerTimeZone(args.timeZone, result.data.siteTimezone),
          },
          boothCameraId: args.boothCameraId,
          roomBCameraId: args.roomBCameraId,
          roomBRaeConditionId: args.roomBRaeConditionId,
          roomBProximityConditionId: args.roomBProximityConditionId,
          selectedWindowId,
        });
        if (bundleHasCompletedCount(next)) {
          lastGoodBundleBySite.set(siteId, next as ShowcaseConditionBundle);
          setBundle(next);
          setStatus('ready');
          return;
        }
        const previous = lastGoodBundleBySite.get(siteId);
        if (previous) {
          setBundle(previous);
          setStatus('ready');
          return;
        }
        setBundle(next);
        setStatus(next ? 'ready' : 'unavailable');
      }).catch(() => {
        if (cancelled) return;
        retainLastGood();
      }).finally(() => {
        if (cancelled) return;
        timer = window.setTimeout(load, QUALIFYING_ENTRY_POLL_MS);
      });
    };

    load();

    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
    };
  }, [
    args.active,
    args.boothCameraId,
    args.roomBCameraId,
    args.roomBProximityConditionId,
    args.roomBRaeConditionId,
    args.timeZone,
    selectedWindowId,
    siteId,
  ]);

  const cachedNow = siteId ? lastGoodBundleBySite.get(siteId) || null : null;
  const resolvedBundle = bundle || cachedNow;
  const resolvedStatus = resolvedBundle && (status === 'idle' || status === 'loading') ? 'ready' : status;

  return {
    status: resolvedStatus,
    bundle: resolvedBundle,
    selected: selectedShowcaseCondition(resolvedBundle, args.source, args.roomBCondition),
    selectedWindowId,
    setSelectedWindowId,
  };
}
